import { delay, take, select, race, put, call } from "redux-saga/effects";
import { selectors } from "reducers";
import { Types } from "actions/auto-dialer";
import { Types as phoneTypes } from "actions/phone";
import { Types as systemTrayTypes, closeTab } from "actions/system-tray";
import { VOICEMAILS_WRAPUP_TIME } from "reducers/auto-dialer";
import { errorMessage } from "actions/message";
import getVoicemailDialerLock from "./voicemail-dialer-lock";
import { checkPatientDialerLockAvailable } from "./dialer-lock";

const getErrorMessage = error =>
  typeof error == "string" ? error : error.message;

export function* watchVoicemailAutoDialerInit() {
  while (true) {
    yield take(Types.VOICEMAIL_AUTODIALER_INIT);

    let releaseLock;
    let lockAcquired = false;
    try {
      const isPatientDialerAvailable = yield call(checkPatientDialerLockAvailable);
      if (!isPatientDialerAvailable) {
        const errorMsg = "Cannot start Voicemail AutoDialer while Patient AutoDialer is running in another tab";
        yield put({
          type: Types.AUTO_DIALER_ERROR,
          payload: errorMsg
        });
        yield put(errorMessage(errorMsg, 3000));
        yield put(closeTab());
        continue;
      }

      const promiseLock = new Promise(res => {
        releaseLock = res;
      });
      yield call(getVoicemailDialerLock, promiseLock);
      lockAcquired = true;
      yield race([
        call(startVoicemailAutoDialer),
        take(Types.VOICEMAIL_AUTODIALER_END)
      ]);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      yield put({
        type: Types.AUTO_DIALER_ERROR,
        payload: errorMsg
      });
      yield put(errorMessage(errorMsg, 3000));
      yield put(closeTab());
    } finally {
      if (lockAcquired && releaseLock) {
        releaseLock();
      }
    }
  }
}

function* startVoicemailAutoDialer() {
  yield put({ type: Types.VOICEMAIL_AUTODIALER_STARTED });
  yield call(proccessNextVoicemail);
}

function* proccessNextVoicemail() {
  const vmailIds = yield select(state =>
    selectors.getAutoDialerVoicemails(state)
  );

  const pageIndex = yield select(state =>
    selectors.getAutoDialerVoicemailsPageIndex(state)
  );

  if (pageIndex >= vmailIds.length) {
    yield call(endVoicemailAutoDialer);
    return;
  }

  const { timeout, worked, paused, manualNav } = yield race({
    timeout: call(voicemailCountdown),
    worked: take(Types.VOICEMAIL_MARKED_WORKED),
    paused: take(phoneTypes.TWILIO_EVENT_CONNECT),
    manualNav: take(Types.AUTO_DIALER_SET_VOICEMAILS_PAGE_INDEX)
  });

  if (paused) {
    const { end } = yield race({
      disconnect: take(phoneTypes.TWILIO_EVENT_DISCONNECT),
      end: take(Types.VOICEMAIL_AUTODIALER_END)
    });
    if (end) return;
    yield call(proccessNextVoicemail);
  } else if (manualNav) {
    yield call(proccessNextVoicemail);
  } else if (timeout || worked) {
    yield call(advanceVoicemailPage);
  }
}

function* voicemailCountdown() {
  let seconds = VOICEMAILS_WRAPUP_TIME;

  while (seconds >= 0) {
    yield put({
      type: Types.VOICEMAIL_AUTODIALER_UPDATE_TIMER,
      payload: { seconds }
    });

    if (seconds === 0) {
      break;
    }

    const { pause, end } = yield race({
      pause: take(Types.VOICEMAIL_AUTODIALER_PAUSE_TIMER),
      end: take(Types.VOICEMAIL_AUTODIALER_END),
      tick: delay(1000)
    });

    if (end) {
      return false;
    }

    if (pause) {
      seconds = VOICEMAILS_WRAPUP_TIME;
      yield put({
        type: Types.VOICEMAIL_AUTODIALER_UPDATE_TIMER,
        payload: { seconds }
      });

      const { end } = yield race({
        resume: take(Types.VOICEMAIL_AUTODIALER_RESUME_TIMER),
        end: take(Types.VOICEMAIL_AUTODIALER_END)
      });
      if (end) return false;
      
      continue;
    }

    --seconds;
  }

  return true;
}

function* advanceVoicemailPage() {
  const vmailIds = yield select(state =>
    selectors.getAutoDialerVoicemails(state)
  );
  const pageIndex = yield select(state =>
    selectors.getAutoDialerVoicemailsPageIndex(state)
  );

  if (pageIndex + 1 >= vmailIds.length) {
    yield call(endVoicemailAutoDialer);
    return;
  }
  yield put({ type: Types.VOICEMAIL_AUTODIALER_NEXT });
  yield call(proccessNextVoicemail);
}

function* endVoicemailAutoDialer() {
  yield put({ type: Types.VOICEMAIL_AUTODIALER_END });
  yield put(closeTab());

}

export function* watchVoicemailAutoDialerCloseTab() {
  while (true) {
    yield take(
      action =>
        action.type === systemTrayTypes.DESTROY_TAB &&
        action.payload.key === "auto-dialer-voicemails"
    );
    const isActive = yield select(state =>
      selectors.getAutodialerVoicemailsActive(state)
    );
    if (isActive) {
      yield put({ type: Types.VOICEMAIL_AUTODIALER_END });
    }
  }
}
